import React, { useState, useEffect } from "react";
import styles from "../../Application.module.scss";
import HeadingWithLine from "../../../../components/HeadingWithLine/HeadingWithLine";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import useCommonStore from "../../../../stores/common";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import {
  formatPhoneNumber,
  formatNumberWithSpaces,
  maskNumber,
  birthdateFormatter,
} from "../../../../../components/helper/helper";
import { titlesOptions } from "../../../../../constants";
import { getCountries } from "../../../../../api";
import CaretRight24 from "../../../../assets/svg/icons/common/CaretRight24";
import IdvStatusBadge, {
  getIdvOutcome,
} from "../../../../components/IdvStatusBadge/IdvStatusBadge";

function PersonalInformation({ appData }) {
  const expandAppDetails = useCommonStore((state) => state.expandDetails);
  const [expanded, setExpanded] = useState(false);
  const isMobile = useMediaQuery(768);

  const [countries, setCountries] = useState([]);

  useEffect(() => {
    getCountries().then((res) => {
      if (res.status) {
        setCountries(res.data);
      }
    });
  }, []);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  if (!appData) {
    return null;
  }

  const getIdType = (type) => { 
    if (type == "PASSPORT") return "Passport";
    if (type == "STATE_ID") return "State ID";
    if (type == "LOCAL_STATE_ID") return "Driver's License";
    if (type == "CITIZENSHIP_CARD") return "Citizenship Card";
    return "-";
  };

  const getCountryName = (country) => {
    if (!country) return "-";
    return countries?.find((c) => c.value == country)?.label || "-";
  };

  const getOwnerTitle = (title) => {
    return titlesOptions.find((t) => t.value == +title)?.label || "-";
  };

  const idvRows = appData?.powerapp_plaid_idv;
  const primaryIdvOutcome = getIdvOutcome(idvRows, {
    first_name: appData?.field_firstname,
    last_name: appData?.field_lastname,
    email: appData?.field_email,
  });

  const renderSection = (title, items, idvOutcome = null) => {
    return (
      <div className={styles.info_section}>
        {title && (
          <>
            <div className={styles.section_title}>
              <h5>{title}</h5>
              <IdvStatusBadge outcome={idvOutcome} />
            </div>
            <div className={styles.desktop_heading}>
              <HeadingWithLine
                text={title}
                badge={<IdvStatusBadge outcome={idvOutcome} />}
              />
            </div>
          </>
        )}
        <div className={styles.info_grid}>
          {items.map((item, index) => (
            <div key={index} className={styles.info_item}>
              <span className={styles.label}>{item.label}</span>
              <span className={styles.value}>{item.value}</span>
            </div>
          ))}
        </div>
        <ul className={styles.desktop_list}>
          {items.map((item, index) => (
            <li key={index}>
              <div>
                <p>{item.label}</p>
                <p>{item.value}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const mainOwnerItems = [
    { label: "Owner Title", value: appData?.title ? getOwnerTitle(appData?.title) : "-" },
    { label: "Name", value: `${appData?.field_firstname || "-"} ${appData?.field_lastname || "-"}` },
    { label: "Personal Phone", value: appData?.field_personalphone ? formatPhoneNumber(appData.field_personalphone) : "-" },
    { label: "Personal Email", value: appData?.field_email || "-" },
    { label: "Personal Street Address", value: appData?.field_personalstreet || "-" },
    { label: "Personal City", value: appData?.field_personalcity || "-" },
    { label: "Personal State", value: appData?.field_personalstate || "-" },
    { label: "Personal Zip", value: appData?.field_personalzip || "-" },
    { label: "Period of Residence", value: `${appData?.field_residenceyears || "-"} year(s) ${appData?.field_residencemonths || "-"} months` },
  ];

  const identityDocItems = [
    { label: "ID Type", value: getIdType(appData?.field_documenttype) || "-" },
    { label: "ID Number", value: appData?.field_documentnumber ? formatNumberWithSpaces(appData.field_documentnumber) : "-" },
    { label: `ID ${appData?.field_documenttype === "PASSPORT" ? "Country" : "State"}`, value: (appData.field_documenttype === "PASSPORT" ? appData?.document_country?.name || "-" : appData.field_driverstate ? appData.field_driverstate || "-" : "-") },
    { label: "Social Security Number", value: appData?.field_socialsecurity ? maskNumber(appData.field_socialsecurity) : "-" },
    { label: "Date of Birth", value: birthdateFormatter(appData?.field_birthdate) },
  ];

  const ownershipItems = [
    { label: "Percentage of ownership", value: `${appData?.field_ownershippercentage || "-"}%` },
  ];

  const getSignerItems = (signer) => {
    return [
      { label: "Owner Title", value: signer?.title ? getOwnerTitle(signer?.title) : "-" },
      { label: "Name", value: `${signer.first_name || "-"} ${signer.last_name || "-"}` },
      { label: "Personal Phone", value: signer.phone || "-" },
      { label: "Personal Email", value: signer.email || "-" },
      { label: "Personal Street Address", value: signer.address_street || "-" },
      { label: "DBA City", value: signer.address_city || "-" },
      { label: "DBA State", value: signer.address_state || "-" },
      { label: "DBA Zip", value: signer.address_zip_code || "-" },
      { label: "Period of Residence", value: `${signer.residence_years || "-"} year(s) ${signer.residence_months || "-"} month(s)` },
    ];
  };

  const getSignerIdentityItems = (signer) => {
    return [
      { label: "ID Type", value: getIdType(signer.document_type) || "-" },
      { label: "ID Number", value: signer.drivers_license_number || "-" },
      { label: `ID ${signer.document_type === "PASSPORT" ? "Country" : "State"}`, value: (signer.document_type === "PASSPORT" ? getCountryName(signer.document_country) : signer.drivers_license_state || "-") },
      { label: "Social Security Number", value: signer.social_security || "-" },
      { label: "Date of Birth", value: birthdateFormatter(signer.date_of_birth) },
    ];
  };

  const getSignerOwnershipItems = (signer) => {
    return [
      { label: "Percentage of ownership", value: `${signer.ownership_percentage || "-"}%` },
    ];
  };

  return (
    <div
      className={`${isMobile ? styles.mobile_business_info : styles.personal_information} ${expandAppDetails ? styles.expanded : ""}`}
    >
      <div className={isMobile ? styles.business_info_header : styles.head} onClick={toggleExpanded}>
        {isMobile ? (
          <>
            <CaretRight24 className={`${styles.caret_icon} ${expanded ? styles.expanded : ""}`} />
            <h4>Personal Information</h4>
          </>
        ) : (
          <>
            <ButtonIcon>
              <CaretRight24 className={expanded ? styles.expanded : ""} />
            </ButtonIcon>
            <h5>Personal Information</h5>
          </>
        )}
      </div>

      <div
        className={`${isMobile ? styles.mobile_content : styles.body} ${expanded ? (isMobile ? styles.expanded : styles.body_expanded) : ""} ${expandAppDetails ? styles.app_details_expanded : ""}`}
      >
        {expanded && (
          <>
            {renderSection(null, mainOwnerItems)}
            {renderSection("Identity document", identityDocItems, primaryIdvOutcome)}
            {renderSection("Ownership", ownershipItems)}
          </>
        )}

        {expanded &&
          appData?.signers?.map((signer, index) => {
            const signerItems = getSignerItems(signer);
            const signerIdentityItems = getSignerIdentityItems(signer);
            const signerOwnershipItems = getSignerOwnershipItems(signer);
            const signerIdvOutcome = getIdvOutcome(idvRows, {
              first_name: signer.first_name,
              last_name: signer.last_name,
              email: signer.email,
            });

            return (
              <div key={index}>
                <div className={styles.info_section}>
                  <div className={styles.section_title}>
                    <h5>Additional owner #{index + 2}</h5>
                    <IdvStatusBadge outcome={signerIdvOutcome} />
                  </div>
                  <h5 className={styles.addl_owner_title}>
                    Additional owner #{index + 2}
                    <IdvStatusBadge outcome={signerIdvOutcome} inline />
                  </h5>
                </div>
                {renderSection(null, signerItems)}
                {renderSection("Identity document", signerIdentityItems)}
                {renderSection("Ownership", signerOwnershipItems)}
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default PersonalInformation;